import moment from "moment";
import { DataTableSkeleton, Pagination } from "@carbon/react";
import { DataTableSkeletonHeader } from "@carbon/react/lib/components/DataTableSkeleton/DataTableSkeleton";

import {
  getCountryNameFromCode,
  getPayoutStatusIndicator,
  truncateStringWithEllipsis,
} from "../../../../utils";
import {
  Badge,
  CopyButton,
  CurrencyDisplay,
  StructuredList,
} from "../../../../components";
import { appConfig } from "../../../../configs";
import {
  IViewPayoutListItem,
  IPageAndSearchMeta,
  StatusColors,
  IPaginationWithQueryString,
} from "../../../../interfaces-and-types";
import { DEFAULT_PAGE_SIZE } from "./LinkedPayouts";

const headers: any /*DataTableSkeletonHeader*/[] = [
  {
    key: "xxxxxxx",
    header: "AMOUNT",
  },
  {
    key: "xxxxxxx",
    header: "STATUS",
  },
  {
    key: "xxxxxxx",
    header: "COUNTRY",
  },
  {
    key: "xxxxxxx",
    header: "BANK",
  },
  {
    key: "xxxxxxx",
    header: "ACCOUNT NO",
  },
  {
    key: "xxxxxxx",
    header: "ACCOUNT NAME",
  },
  {
    key: "xxxxxxx",
    header: "PAID ON",
  },
];

export const View = ({
  isLoading,
  isError,
  pageMeta,
  paginationContext,
  transactionPayouts,
}: {
  isLoading: boolean;
  isError: boolean;
  pageMeta?: IPageAndSearchMeta;
  paginationContext: IPaginationWithQueryString;
  transactionPayouts: IViewPayoutListItem[];
}) => {
  if (isLoading)
    return (
      <DataTableSkeleton
        columnCount={7}
        rowCount={2}
        headers={headers}
        showHeader={false}
        showToolbar={false}
      />
    );

  return (
    <>
      <StructuredList
        headers={
          <div className="cds--structured-list-row cds--structured-list-row--header-row">
            <div className="cds--structured-list-th align-text--center">
              AMOUNT
            </div>
            {/* <div className="cds--structured-list-th">COUNTRY</div> */}
            <div className="cds--structured-list-th">BANK</div>
            <div className="cds--structured-list-th">ACCOUNT NO</div>
            <div className="cds--structured-list-th">ACCOUNT NAME</div>
            <div className="cds--structured-list-th">STATUS</div>
            {/* <div className="cds--structured-list-th">CREATED</div> */}
            <div className="cds--structured-list-th">PAID ON</div>
          </div>
        }
        {...{
          rows: transactionPayouts.map((payout) => {
            const statusIndicator: {
              statusText: string;
              statusType: StatusColors;
            } = getPayoutStatusIndicator(payout);

            //   const payoutCountry =
            //     getCountryNameFromCode(payout.country) ?? payout.country;

            return (
              <div className="cds--structured-list-tbody">
                <div className="cds--structured-list-row">
                  <div className="cds--structured-list-td align-text--right">
                    <CurrencyDisplay
                      amount={payout.amount}
                      currencyOrSymbol={payout.currency}
                      amountFontSize={16}
                      symbolFontSize={12}
                      amountClassName={""}
                      symbolClassName={""}
                    />
                  </div>

                  {/* <div className="cds--structured-list-td" title={payoutCountry}>
                  <div className="flex-container justify-content--start align-items--center no-wrap">
                    <div className="transaction-id-text modal-header--title">
                      <span>
                        {truncateStringWithEllipsis(payoutCountry, 15)}
                      </span>
                    </div>
                  </div>
                </div> */}

                  <div
                    className="cds--structured-list-td"
                    title={
                      payout.settled_to_wallet
                        ? "This payout was settled to the wallet of same currency"
                        : payout.bank_name
                    }
                  >
                    <div className="flex-container justify-content--start align-items--center no-wrap">
                      <div className="transaction-id-text modal-header--title">
                        <span title={payout.bank_name}>
                          {payout.settled_to_wallet ? (
                            <span className="text-primary font-ctm--bold">
                              WALLET SETTLEMENT
                            </span>
                          ) : (
                            truncateStringWithEllipsis(payout.bank_name, 20)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="cds--structured-list-td"
                    title={payout.account_number}
                  >
                    <div className="flex-container justify-content--start align-items--center no-wrap">
                      <div className="transaction-id-text modal-header--title">
                        <span title={payout.account_number}>
                          {truncateStringWithEllipsis(
                            payout.account_number,
                            15
                          )}
                        </span>
                      </div>
                      <div className="copy-button--container ml-3">
                        <CopyButton
                          name="copy-account-button"
                          stringToCopy={payout.account_number}
                          iconDescription="copy account no"
                          className="copy-btn"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="cds--structured-list-td"
                    title={payout.account_name}
                  >
                    <div className="flex-container justify-content--start align-items--center no-wrap">
                      <div className="transaction-id-text modal-header--title">
                        <span title={payout.account_name}>
                          {truncateStringWithEllipsis(payout.account_name, 15)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="cds--structured-list-td">
                    <Badge
                      text={statusIndicator.statusText}
                      statusColor={statusIndicator.statusType}
                      hideIndicator
                      className=""
                    />
                  </div>

                  {/* <div className="cds--structured-list-td">
                  {moment(payout.created_at).format(appConfig.dateTimeFormat)}
                </div> */}

                  <div className="cds--structured-list-td">
                    {!payout.paid_at
                      ? "..."
                      : moment(payout.paid_at).format(appConfig.dateTimeFormat)}
                  </div>
                </div>
              </div>
            );
          }),
        }}
      />

      {!pageMeta?.item_count ||
      pageMeta?.item_count < DEFAULT_PAGE_SIZE ? null : (
        <Pagination
          totalItems={pageMeta?.item_count ? pageMeta.item_count : 0}
          // backwardText="Previous"
          // forwardText="Next"
          page={paginationContext.currentPage}
          pageSize={paginationContext.currentPageSize}
          pageSizes={[5, DEFAULT_PAGE_SIZE]}
          itemsPerPageText="per page"
          onChange={({
            page,
            pageSize,
          }: {
            page: number;
            pageSize: number;
          }) => {
            paginationContext.setCurrentPage(page);
            // paginationContext.setFirstRowIndex(pageSize * (page - 1));

            if (pageSize !== paginationContext.currentPageSize) {
              paginationContext.setCurrentPageSize(pageSize);
            }
          }}
          size="sm"
        />
      )}
    </>
  );
};
